import { useEffect, useState } from "react";
import "./CrudContentPopup.scss";
import ModalContainer from "../ModalContainer";
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import useNotification from "../../../hooks/notification.hook";
import usePlanContentPresetsCopy from "../../../hooks/plan-content-presets-copy.hook";
import BoxPlanOrganizationTemplate from "../BoxPlanOrganizationTemplate";
import {
	createTemplatesPlansContentPresetImportConfiguration,
	editTemplatesPlansContentPresetImportConfiguration,
} from "../../../services/plans-template-content-preset-import-configuration.service";
import { getPresetSelection } from "../../../helper/formatter.helper";

export default function CrudContentModal({ open, setOpen, confModel, template }) {
	const { t } = useTranslation();

	const { setUpdateListConf, planData } = usePlanContentPresetsCopy();

	// Set message
	yup.setLocale({
		// use constant translation keys for messages without values
		mixed: {
			required: ({ path }) => t("common:errors.required", { field: t(`common:fields.${path}`) }),
		},
		number: {
			moreThan: ({ path, more }) =>
				t("common:errors.more_than", {
					field: t(`common:fields.${path}`),
					value: more,
				}),
			lessThan: ({ path, less }) =>
				t("common:errors.less_than", {
					field: t(`common:fields.${path}`),
					value: less,
				}),
		},
	});

	// Schema Yup
	const schema = yup.object().shape({
		title: yup.string().required(),
	});

	const {
		register,
		setValue,
		trigger,
		handleSubmit,
		formState: { errors },
	} = useForm({ resolver: yupResolver(schema) });

	const { openNotification, helperErrorNotification } = useNotification();

	const [loading, setLoading] = useState(false);

	const [selectedChapters, setSelectedChapters] = useState({
		chapters: [],
		contents: [],
		goals: [],
		reqs: [],
	});

	/**
	 * Form Handler
	 * @param {form modal} data
	 */
	async function confirmCopy(data) {
		if (selectedChapters.chapters.length > 0 && !loading) {
			setLoading(true);

			const content_selections = selectedChapters.contents.map((c) => ({
				template_chapter_content_id: c.id,
			}));

			const requirement_selections = selectedChapters.reqs.map((r) => ({
				template_requirement_id: r.id,
			}));

			if (!confModel?.id) {
				await createTemplatesPlansContentPresetImportConfiguration({
					templatePlan: planData?.template_plan_id,
					contentPreset: planData?.content_preset_id,
					selection_mode: "explicit",
					content_selections,
					requirement_selections,
					...data,
				});
			} else {
				await editTemplatesPlansContentPresetImportConfiguration({
					templatePlan: planData?.template_plan_id,
					contentPreset: planData?.content_preset_id,
					importConfiguration: confModel.id,
					selection_mode: "explicit",
					lock_version: confModel.lock_version,
					content_selections,
					requirement_selections,
					...data,
				});
			}

			setUpdateListConf((i) => i + 1);

			setLoading(false);

			setOpen(false);
		} else {
			openNotification({
				text: "Nessun contenuto da copiare selezionato",
				status: "error",
			});
		}
	}

	useEffect(() => {
		if (open) {
			setValue("title", confModel ? confModel.title : "");

			if (!confModel) {
				setSelectedChapters({
					chapters: [],
					contents: [],
					goals: [],
					reqs: [],
				});
			} else {
				const modelReqs = confModel.requirement_selections?.map((r) => r.template_requirement_id);
				const modelContents = confModel.content_selections?.map(
					(c) => c.template_chapter_content_id
				);

				setSelectedChapters(getPresetSelection(template, modelReqs, modelContents));
			}
		}
	}, [open]);

	/**
	 * Errors Form
	 */
	useEffect(() => {
		if (Object.keys(errors).length) {
			const fields = Object.values(errors).map((er) => er.message);
			openNotification({ fields, status: "error" });
		}
	}, [errors]);

	return (
		<ModalContainer isOpen={open} setIsOpen={setOpen} disableClose={loading}>
			<div className="main_modal_content conf_plan_popup">
				<button
					className="main_close"
					onClick={() => !loading && setOpen(false)}
					type="button"
					aria-label="Chiudi"
				>
					<span className="icon_10 default close icon_mask !bg-white"></span>
				</button>

				<div className="main_header_modal">
					<span className="title">
						{confModel?.id ? "Modifica configurazione" : "Nuova configurazione"}
					</span>

					<div className="main_action_detail">
						<form
							id="content-conf-form"
							onSubmit={handleSubmit(confirmCopy)}
							className="global_form_landing"
						>
							<div className="base">
								<div className="wrap_input_landing">
									<label className="label_input" htmlFor="name_conf">
										Nome
									</label>
									<input
										type="text"
										{...register("title")}
										className={`landing${errors.title ? " err" : ""}`}
										id="name_conf"
									/>
								</div>
							</div>
						</form>
					</div>
				</div>

				<div className={"main_body_modal"}>
					<OverlayScrollbarsComponent
						defer
						options={{ scrollbars: { autoHide: "scroll" } }}
						className="main_list_capthers"
					>
						{/* Chapters */}
						<BoxPlanOrganizationTemplate
							template={template}
							setSelectedItems={setSelectedChapters}
							showAllContents={false}
							selectedItems={selectedChapters}
						/>
					</OverlayScrollbarsComponent>
				</div>

				<div className="main_footer_modal">
					<div className="right_content">
						{/* Main Cta */}
						<button
							type="submit"
							form="content-conf-form"
							className={`button fill success${loading ? " loading" : ""}`}
						>
							<div className="load_content">Conferma</div>
							<div className="main_loader_icon fade_in"></div>
						</button>
					</div>
				</div>
			</div>
		</ModalContainer>
	);
}
